import { ConnectedPosition } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { asyncScheduler, filter, Observable } from 'rxjs';

import { BaseFormControlComponent } from '@shared/components/base-form-control/base-form-control';
import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { TrialStatusMap } from '@shared/constants/status.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { Trial, TrialImplementationStatus, TrialWorkspaceType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { UnsavedChangesHelperService } from '@shared/services/unsaved-changes-helper.service';
import { MainQuery } from '@shared/store/main/main.query';
import { selectWithGuardChangesModalCheck } from '@shared/utils/select-option';
import { RequireSome, Utils } from '@shared/utils/utils';

type TrialDropdownItem =
  | (Omit<RequireSome<Partial<Trial>, 'id' | 'workspace_type'>, 'sponsor_organization'> & {
      sponsor_organization: { id: string; name: string };
    })
  | {
      id: string | undefined;
      implementation_status?: string;
      onboarding_complete?: boolean | undefined;
      short_name?: string | undefined;
      workspace_type?: TrialWorkspaceType;
    };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectModule,
    ReactiveFormsModule,
    CommonModule,
    TooltipDirective,
    IconComponent,
    InputComponent,
  ],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TrialDropdownComponent),
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TrialDropdownComponent),
    },
  ],
  selector: 'aux-trial-dropdown',
  styleUrls: ['trial-dropdown.component.scss'],
  templateUrl: './trial-dropdown.component.html',
})
export class TrialDropdownComponent
  extends BaseFormControlComponent<null | string>
  implements AfterViewInit
{
  private mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);
  private readonly unsavedChangesHelperService = inject(UnsavedChangesHelperService);

  selectedTrialIcon = signal<AuxIconName>('BuildingHospital');
  selectedTrialLabel = signal('TRIAL');
  sortedTrials = signal<TrialDropdownItem[]>([]);

  groupByFn = computed(() => {
    const recentSelectedTrialId = this.mainQuery.trialKey();

    return (option: TrialDropdownItem) => {
      if (option.id === recentSelectedTrialId) {
        return null;
      }

      switch (option.workspace_type) {
        case TrialWorkspaceType.WORKSPACE_TYPE_CLINICAL_TRIAL:
          return 'CLINICAL TRIALS';
        case TrialWorkspaceType.WORKSPACE_TYPE_CMC:
          return 'CMC';
        default:
          return null;
      }
    };
  });

  @Input() mainPage = true;
  tooltipColor = '';
  tooltipPosition = {
    offsetY: 8,
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  } as ConnectedPosition;
  tooltipTextColor = '';
  tooltipValue = '';
  @ViewChild('trialDropdownEl') trialDropdownEl!: NgSelectComponent;
  @Input() trialFormControl = new UntypedFormControl('');
  @Input() trials$!: Observable<TrialDropdownItem[]>;
  trialStatusMap = TrialStatusMap;

  private trials: TrialDropdownItem[] = [];

  ngAfterViewInit() {
    this.trialFormControl.valueChanges.subscribe((value) => {
      this.onTrialChange(value);
      this.enforceRegroup();
    });
    this.trials$.subscribe((trials) => {
      this.trials = trials.sort((a, b) =>
        Utils.alphaNumSort(a.short_name || '', b.short_name || '')
      );
      this.sortedTrials.set(this.trials);
      this.onTrialChange(this.trialFormControl.value);
    });
  }

  constructor() {
    super();
    this.mainQuery
      .select('trialKey')
      .pipe(takeUntilDestroyed(), filter(Boolean))
      .subscribe(() => this.setSearchTermManualy('', false));
  }

  enforceRegroup() {
    const ENFORCE_REGROUP_DELAY = 100;

    this.sortedTrials.set([]);
    setTimeout(() => {
      this.sortedTrials.set(this.trials);
    }, ENFORCE_REGROUP_DELAY);
  }

  focusSearchInput() {
    setTimeout(() => {
      const searchInput = document.querySelector('.search-input') as HTMLElement;

      searchInput?.focus();
    }, 0);
  }

  isKeyEvent(event: KeyboardEvent): event is KeyboardEvent & { target: HTMLInputElement } {
    return 'target' in event;
  }

  mouseEnter(implementation_status: TrialImplementationStatus) {
    this.tooltipColor = this.trialStatusMap[implementation_status].color;
    this.tooltipTextColor = this.trialStatusMap[implementation_status].textColor;
    this.tooltipValue = this.trialStatusMap[implementation_status].tooltipValue;
  }

  private onTrialChange(selectedId: string) {
    const type: TrialWorkspaceType =
      (this.trials.find(({ id }) => selectedId === id) as Trial)?.workspace_type ||
      TrialWorkspaceType.WORKSPACE_TYPE_CLINICAL_TRIAL;

    this.selectedTrialIcon.set(
      type === TrialWorkspaceType.WORKSPACE_TYPE_CMC ? 'VirusSearch' : 'BuildingHospital'
    );
    this.selectedTrialLabel.set(type === TrialWorkspaceType.WORKSPACE_TYPE_CMC ? 'CMC' : 'TRIAL');
  }

  selectTrial(event: MouseEvent, value: string): Promise<void> {
    return selectWithGuardChangesModalCheck({
      element: this.trialDropdownEl,
      event,
      formControl: this.trialFormControl,
      hasUnsavedChanges: this.unsavedChangesHelperService.getUnsavedChangesDecisionByRouterUrl(),
      overlayService: this.overlayService,
      value,
    })
      .then(() => this.onTrialChange(value))
      .catch(console.error);
  }

  setSearchTermManualy(searchTerm: string | undefined, async = true) {
    if (!this.trialDropdownEl) {
      return;
    }

    const setSearchTerm = () => {
      this.trialDropdownEl.searchTerm = searchTerm || '';
      this.trialDropdownEl.blur();
      this.trialDropdownEl.detectChanges();
    };

    if (async) {
      asyncScheduler.schedule(setSearchTerm);

      return;
    }

    setSearchTerm();
  }

  typecastingForTrialStatus(item_status: unknown): TrialImplementationStatus {
    const implementation_status = item_status as TrialImplementationStatus;

    return implementation_status;
  }
}
